import React, { useCallback, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// Futures snap values for the leverage slider
const FUTURES_SNAP_VALUES = [1, 5, 10, 25, 50, 100];
interface OrderFormProps {
  mode?: 'spot' | 'margin' | 'futures';
  availableBalance?: number;
  currentPrice?: number;
  currency?: string;
  onSubmitOrder?: (order: any) => void;
}
export function OrderForm({
  mode = 'spot',
  availableBalance = 79.19,
  currentPrice = 96470,
  currency = 'USD',
  onSubmitOrder
}: OrderFormProps) {
  const [orderSide, setOrderSide] = useState<'buy' | 'sell'>('buy');
  const [orderType, setOrderType] = useState('Market');
  const [limitPrice, setLimitPrice] = useState('');
  const [fiatAmount, setFiatAmount] = useState('');
  const [cryptoAmount, setCryptoAmount] = useState('');
  const [leverage, setLeverage] = useState(1);
  const [marginMode, setMarginMode] = useState<'cross' | 'isolated'>('cross');
  const [postOnly, setPostOnly] = useState(false);
  const [reduceOnly, setReduceOnly] = useState(false);
  const [takeProfit, setTakeProfit] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [showTpSl, setShowTpSl] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  // Derive margin enabled from leverage
  const marginEnabled = leverage > 1;
  // Snap to nearest futures value
  const snapToFuturesValue = (val: number): number => {
    let closest = FUTURES_SNAP_VALUES[0];
    let minDist = Math.abs(val - closest);
    for (const sv of FUTURES_SNAP_VALUES) {
      const dist = Math.abs(val - sv);
      if (dist < minDist) {
        minDist = dist;
        closest = sv;
      }
    }
    return closest;
  };
  const handleLeverageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = parseInt(e.target.value, 10);
    if (mode === 'futures') {
      setLeverage(snapToFuturesValue(raw));
    } else {
      setLeverage(raw);
    }
  };
  // Slider config per mode
  const sliderMin = 1;
  const sliderMax = mode === 'futures' ? 100 : 10;
  const sliderStep = 1;
  // Calculate filled percentage for slider track
  const filledPercent = (leverage - sliderMin) / (sliderMax - sliderMin) * 100;
  // Calculate conversion
  const effectivePrice = orderType === 'Limit' && parseFloat(limitPrice) > 0 ? parseFloat(limitPrice) : currentPrice || 96470;
  const fiatValue = parseFloat(fiatAmount) || 0;
  const cryptoValue = parseFloat(cryptoAmount) || 0;
  const conversionText = fiatValue > 0 ? `≈ ${(fiatValue / effectivePrice).toFixed(8)} BTC` : cryptoValue > 0 ? `≈ ${(cryptoValue * effectivePrice).toFixed(2)} ${currency}` : '';
  // Fee calculations
  const tradingFee = fiatValue * 0.002;
  const requiredMargin = mode !== 'spot' && marginEnabled ? fiatValue / leverage : 0;
  const marginFee = mode !== 'spot' && marginEnabled ? fiatValue * 0.0001 : 0;
  // Mock margin health (green→yellow→red)
  const marginHealth = mode !== 'spot' && marginEnabled ? 75 : 100;
  const getMarginHealthColor = () => {
    if (marginHealth > 50) return 'bg-success';
    if (marginHealth > 20) return 'bg-warning';
    return 'bg-danger';
  };
  // Mock liquidation price for futures
  const liquidationPrice = mode === 'futures' && marginEnabled && fiatValue > 0 ? orderSide === 'buy' ? effectivePrice * 0.92 : effectivePrice * 1.08 : null;
  const handleSubmit = useCallback(() => {
    onSubmitOrder?.({
      mode,
      side: orderSide,
      type: orderType,
      price: effectivePrice,
      amount: cryptoValue,
      total: fiatValue,
      leverage: mode !== 'spot' && marginEnabled ? leverage : 1
    });
  }, [onSubmitOrder, mode, orderSide, orderType, effectivePrice, cryptoValue, fiatValue, leverage, marginEnabled]);
  return <div className="flex flex-col h-full" data-id="element-2115">
      {/* Main Form Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-2116">
        {/* Buy/Sell Toggle - Revolut X style */}
        <div className="flex items-center gap-2 bg-background-tertiary rounded-lg p-1" data-id="element-2117">
          <button onClick={() => setOrderSide('buy')} className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${orderSide === 'buy' ? 'bg-success text-white shadow-sm' : 'bg-transparent text-text-secondary hover:text-text-primary'}`} data-id="element-2118">
            Buy
          </button>
          <button onClick={() => setOrderSide('sell')} className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${orderSide === 'sell' ? 'bg-danger text-white shadow-sm' : 'bg-transparent text-text-secondary hover:text-text-primary'}`} data-id="element-2119">
            Sell
          </button>
        </div>

        {/* Order Type Dropdown - aligned right */}
        <div className="flex items-center justify-end" data-id="element-2120">
          <div className="relative" data-id="element-2121">
            <select value={orderType} onChange={e => setOrderType(e.target.value)} className="appearance-none bg-background-tertiary rounded-lg px-3 py-1.5 pr-8 text-sm text-text-primary border border-transparent hover:border-border-default focus:border-brand-purple focus:outline-none input-focus-glow transition-colors cursor-pointer" data-id="element-2122">
              <option value="Market" data-id="element-2123">Market</option>
              <option value="Limit" data-id="element-2124">Limit</option>
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" data-id="element-2125" />
          </div>
        </div>

        {/* Limit Price Input - shown when Limit order type is selected */}
        {orderType === 'Limit' && <div data-id="element-2126">
            <label className="block text-xs text-text-secondary mb-2" data-id="element-2127">
              Limit Price
            </label>
            <div className="relative" data-id="element-2128">
              <input type="text" value={limitPrice} onChange={e => setLimitPrice(e.target.value)} className="w-full bg-background-tertiary rounded-xl px-4 py-3 pr-16 text-sm text-text-primary border border-border-subtle focus:border-brand-purple focus:outline-none input-focus-glow transition-colors" placeholder={effectivePrice.toLocaleString()} data-id="element-2129" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-text-secondary pointer-events-none" data-id="element-2130">
                USD
              </span>
            </div>
          </div>}

        {/* Cross/Isolated Toggle (Futures mode only) */}
        {mode === 'futures' && <div className="flex items-center gap-1 bg-background-tertiary rounded-lg p-1" data-id="element-2131">
            <button onClick={() => setMarginMode('cross')} className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${marginMode === 'cross' ? 'bg-background-secondary text-text-primary shadow-sm' : 'bg-transparent text-text-secondary hover:text-text-primary'}`} data-id="element-2132">
              Cross
            </button>
            <button onClick={() => setMarginMode('isolated')} className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${marginMode === 'isolated' ? 'bg-background-secondary text-text-primary shadow-sm' : 'bg-transparent text-text-secondary hover:text-text-primary'}`} data-id="element-2133">
              Isolated
            </button>
          </div>}

        {/* Leverage Slider (Margin & Futures modes) */}
        {mode !== 'spot' && <div data-id="element-2134">
            <div className="flex items-center justify-between mb-2" data-id="element-2135">
              <span className="text-sm text-text-secondary" data-id="element-2136">Leverage</span>
              <span className="text-sm text-text-primary font-mono" data-id="element-2137">
                {leverage}x
              </span>
            </div>
            <div className="relative w-full h-4 flex items-center" data-id="element-2138">
              <input type="range" min={sliderMin} max={sliderMax} step={sliderStep} value={leverage} onChange={handleLeverageChange} className="leverage-slider w-full" style={{
            '--filled-percent': `${filledPercent}%`
          } as React.CSSProperties} data-id="element-2139" />
            </div>
            {mode === 'futures' && <div className="relative w-full h-5 mt-1.5" data-id="element-2140">
                {FUTURES_SNAP_VALUES.map(v => {
            const pct = (v - sliderMin) / (sliderMax - sliderMin) * 100;
            return <button key={v} onClick={() => setLeverage(v)} className={`absolute text-[10px] font-mono transition-colors -translate-x-1/2 ${leverage === v ? 'text-brand-purple-light' : 'text-text-tertiary hover:text-text-secondary'}`} style={{
              left: `${pct}%`
            }} data-id="element-2141">
                      {v}x
                    </button>;
          })}
              </div>}
          </div>}

        {/* Input Fields - Fiat Amount */}
        <div data-id="element-2142">
          <div className="relative" data-id="element-2143">
            <input type="text" value={fiatAmount} onChange={e => {
            setFiatAmount(e.target.value);
            const val = parseFloat(e.target.value) || 0;
            setCryptoAmount(val > 0 ? (val / effectivePrice).toFixed(8) : '');
          }} className="w-full bg-background-tertiary rounded-xl px-4 py-3 pr-16 text-sm text-text-primary border border-border-subtle focus:border-brand-purple focus:outline-none input-focus-glow transition-colors" placeholder="0.00" data-id="element-2144" />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-text-secondary pointer-events-none" data-id="element-2145">
              {currency}
            </span>
          </div>
        </div>

        {/* Conversion Text */}
        {conversionText && <div className="text-xs text-text-tertiary text-center -mt-2" data-id="element-2146">
            {conversionText}
          </div>}

        {/* Input Fields - Crypto Amount */}
        <div data-id="element-2147">
          <div className="relative" data-id="element-2148">
            <input type="text" value={cryptoAmount} onChange={e => {
            setCryptoAmount(e.target.value);
            const val = parseFloat(e.target.value) || 0;
            setFiatAmount(val > 0 ? (val * effectivePrice).toFixed(2) : '');
          }} className="w-full bg-background-tertiary rounded-xl px-4 py-3 pr-16 text-sm text-text-primary border border-border-subtle focus:border-brand-purple focus:outline-none input-focus-glow transition-colors" placeholder="0.00000000" data-id="element-2149" />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-text-secondary pointer-events-none" data-id="element-2150">
              BTC
            </span>
          </div>
        </div>

        {/* Available Balance */}
        <div className="flex items-center justify-between text-sm" data-id="element-2151">
          <span className="text-text-secondary" data-id="element-2152">Available</span>
          <span className="text-text-secondary" data-id="element-2153">
            {currency} {availableBalance.toFixed(2)}
          </span>
        </div>

        {/* Margin Health Bar (Margin mode only) */}
        {mode === 'margin' && marginEnabled && <div data-id="element-2154">
            <div className="flex items-center justify-between text-xs mb-2" data-id="element-2155">
              <span className="text-text-secondary" data-id="element-2156">Margin health</span>
              <span className="text-text-primary font-mono" data-id="element-2157">
                {marginHealth.toFixed(0)}%
              </span>
            </div>
            <div className="w-full h-1.5 bg-background-tertiary rounded-full overflow-hidden" data-id="element-2158">
              <div className={`h-full rounded-full transition-all ${getMarginHealthColor()}`} style={{
            width: `${marginHealth}%`
          }} data-id="element-2159" />
            </div>
          </div>}

        {/* Margin Health Bar (Futures mode only) */}
        {mode === 'futures' && marginEnabled && <div data-id="element-2160">
            <div className="flex items-center justify-between text-xs mb-2" data-id="element-2161">
              <span className="text-text-secondary" data-id="element-2162">Margin health</span>
              <span className="text-text-primary font-mono" data-id="element-2163">
                {marginHealth.toFixed(0)}%
              </span>
            </div>
            <div className="w-full h-1.5 bg-background-tertiary rounded-full overflow-hidden" data-id="element-2164">
              <div className={`h-full rounded-full transition-all ${getMarginHealthColor()}`} style={{
            width: `${marginHealth}%`
          }} data-id="element-2165" />
            </div>
          </div>}

        {/* Post Only Toggle (Margin mode only) */}
        {mode === 'margin' && <div className="flex items-center justify-between" data-id="element-2166">
            <span className="text-sm text-text-secondary" data-id="element-2167">Post only</span>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-2168">
              <input type="checkbox" checked={postOnly} onChange={e => setPostOnly(e.target.checked)} className="sr-only peer" data-id="element-2169" />
              <div className="w-9 h-5 bg-background-tertiary peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand-purple" data-id="element-2170"></div>
            </label>
          </div>}

        {/* Post Only & Reduce Only Toggles (Futures mode only) */}
        {mode === 'futures' && <div data-id="element-2171">
            <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className="text-sm text-brand-purple-lighter hover:text-white transition-colors font-medium" data-id="element-2172">
              {showAdvancedOptions ? '− Advanced options' : '+ Advanced options'}
            </button>
            <AnimatePresence data-id="element-2173">
              {showAdvancedOptions && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: [0.4, 0, 0.2, 1]
          }} className="overflow-hidden" data-id="element-2174">
                  <div className="space-y-3 pt-3" data-id="element-2175">
                    <div className="flex items-center justify-between" data-id="element-2176">
                      <span className="text-sm text-text-secondary" data-id="element-2177">
                        Post only
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-2178">
                        <input type="checkbox" checked={postOnly} onChange={e => setPostOnly(e.target.checked)} className="sr-only peer" data-id="element-2179" />
                        <div className="w-9 h-5 bg-background-tertiary peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand-purple" data-id="element-2180"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-2181">
                      <span className="text-sm text-text-secondary" data-id="element-2182">
                        Reduce only
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-2183">
                        <input type="checkbox" checked={reduceOnly} onChange={e => setReduceOnly(e.target.checked)} className="sr-only peer" data-id="element-2184" />
                        <div className="w-9 h-5 bg-background-tertiary peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand-purple" data-id="element-2185"></div>
                      </label>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>}

        {/* TP/SL - Spot mode (collapsible) */}
        {mode === 'spot' && <div data-id="element-2186">
            <button onClick={() => setShowTpSl(!showTpSl)} className="text-sm text-brand-purple-lighter hover:text-white transition-colors font-medium" data-id="element-2187">
              {showTpSl ? '− Remove TP/SL' : '+ Add TP/SL'}
            </button>
            <AnimatePresence data-id="element-2188">
              {showTpSl && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: [0.4, 0, 0.2, 1]
          }} className="overflow-hidden" data-id="element-2189">
                  <div className="space-y-3 pt-3" data-id="element-2190">
                    <div data-id="element-2191">
                      <label className="block text-xs text-text-secondary mb-2" data-id="element-2192">
                        Take Profit
                      </label>
                      <div className="relative" data-id="element-2193">
                        <input type="text" value={takeProfit} onChange={e => setTakeProfit(e.target.value)} className="w-full bg-background-tertiary rounded-xl px-4 py-3 pr-16 text-sm text-text-primary border border-border-subtle focus:border-brand-purple focus:outline-none input-focus-glow transition-colors" placeholder="0.00" data-id="element-2194" />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-text-secondary pointer-events-none" data-id="element-2195">
                          USD
                        </span>
                      </div>
                    </div>
                    <div data-id="element-2196">
                      <label className="block text-xs text-text-secondary mb-2" data-id="element-2197">
                        Stop Loss
                      </label>
                      <div className="relative" data-id="element-2198">
                        <input type="text" value={stopLoss} onChange={e => setStopLoss(e.target.value)} className="w-full bg-background-tertiary rounded-xl px-4 py-3 pr-16 text-sm text-text-primary border border-border-subtle focus:border-brand-purple focus:outline-none input-focus-glow transition-colors" placeholder="0.00" data-id="element-2199" />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-text-secondary pointer-events-none" data-id="element-2200">
                          USD
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>}

        {/* TP/SL - Futures mode (collapsible) */}
        {mode === 'futures' && <div data-id="element-2201">
            <button onClick={() => setShowTpSl(!showTpSl)} className="text-sm text-brand-purple-lighter hover:text-white transition-colors font-medium" data-id="element-2202">
              {showTpSl ? '− Remove TP/SL' : '+ Add TP/SL'}
            </button>
            <AnimatePresence data-id="element-2203">
              {showTpSl && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: [0.4, 0, 0.2, 1]
          }} className="overflow-hidden" data-id="element-2204">
                  <div className="space-y-3 pt-3" data-id="element-2205">
                    <div data-id="element-2206">
                      <label className="block text-xs text-text-secondary mb-2" data-id="element-2207">
                        Take Profit
                      </label>
                      <div className="relative" data-id="element-2208">
                        <input type="text" value={takeProfit} onChange={e => setTakeProfit(e.target.value)} className="w-full bg-background-tertiary rounded-xl px-4 py-3 pr-16 text-sm text-text-primary border border-border-subtle focus:border-brand-purple focus:outline-none input-focus-glow transition-colors" placeholder="0.00" data-id="element-2209" />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-text-secondary pointer-events-none" data-id="element-2210">
                          USD
                        </span>
                      </div>
                    </div>
                    <div data-id="element-2211">
                      <label className="block text-xs text-text-secondary mb-2" data-id="element-2212">
                        Stop Loss
                      </label>
                      <div className="relative" data-id="element-2213">
                        <input type="text" value={stopLoss} onChange={e => setStopLoss(e.target.value)} className="w-full bg-background-tertiary rounded-xl px-4 py-3 pr-16 text-sm text-text-primary border border-border-subtle focus:border-brand-purple focus:outline-none input-focus-glow transition-colors" placeholder="0.00" data-id="element-2214" />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-text-secondary pointer-events-none" data-id="element-2215">
                          USD
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>}

        {/* Funding Rate & Est. Liquidation Price (Futures mode only) */}
        {mode === 'futures' && <div className="space-y-2" data-id="element-2216">
            <div className="text-xs text-text-tertiary" data-id="element-2217">
              Funding: 0.01% in 2h 34m
            </div>
            {liquidationPrice && <div className="flex items-center justify-between text-xs" data-id="element-2218">
                <span className="text-text-tertiary" data-id="element-2219">
                  Est. Liquidation Price
                </span>
                <span className="text-warning font-mono" data-id="element-2220">
                  {liquidationPrice.toLocaleString()} {currency}
                </span>
              </div>}
          </div>}
      </div>

      {/* Fixed Bottom Section */}
      <div className="border-t border-border-subtle p-4 space-y-3" data-id="element-2221">
        {/* Order Summary - only show if amount entered */}
        {fiatValue > 0 && <div className="space-y-2" data-id="element-2222">
            <div className="flex items-center justify-between text-xs" data-id="element-2223">
              <span className="text-text-tertiary" data-id="element-2224">Quantity</span>
              <span className="text-text-secondary font-mono" data-id="element-2225">
                {cryptoValue.toFixed(8)} BTC
              </span>
            </div>
            <div className="flex items-center justify-between text-xs" data-id="element-2226">
              <span className="text-text-tertiary" data-id="element-2227">Estimated price</span>
              <span className="text-text-secondary font-mono" data-id="element-2228">
                {effectivePrice.toLocaleString()} {currency}
              </span>
            </div>
            {mode !== 'spot' && marginEnabled && <div className="flex items-center justify-between text-xs" data-id="element-2229">
                <span className="text-text-tertiary" data-id="element-2230">Required margin</span>
                <span className="text-text-secondary font-mono" data-id="element-2231">
                  {requiredMargin.toFixed(2)} {currency}
                </span>
              </div>}
            <div className="flex items-center justify-between text-xs" data-id="element-2232">
              <span className="text-text-tertiary" data-id="element-2233">Estimated fee</span>
              <span className="text-text-secondary font-mono" data-id="element-2234">
                {tradingFee.toFixed(2)} {currency}
              </span>
            </div>
            {mode !== 'spot' && marginEnabled && <div className="flex items-center justify-between text-xs" data-id="element-2235">
                <span className="text-text-tertiary" data-id="element-2236">Est. margin fee</span>
                <span className="text-text-secondary font-mono" data-id="element-2237">
                  {marginFee.toFixed(2)} {currency}
                </span>
              </div>}
            <div className="flex items-center justify-between text-xs" data-id="element-2238">
              <span className="text-text-tertiary" data-id="element-2239">Value</span>
              <span className="text-text-secondary font-mono" data-id="element-2240">
                {fiatValue.toFixed(2)} {currency}
              </span>
            </div>
          </div>}

        {/* Submit Button */}
        <button onClick={handleSubmit} className={`w-full py-3 rounded-xl font-semibold text-base transition-all ${orderSide === 'buy' ? 'bg-success hover:brightness-110 text-white' : 'bg-danger hover:brightness-110 text-white'}`} data-id="element-2241">
          {orderSide === 'buy' ? 'Buy' : 'Sell'} BTC
          {mode !== 'spot' && marginEnabled && ` (${leverage}x)`}
        </button>
      </div>
    </div>;
}